import { Button, FormField, Surface } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReceiptDetail, ReceiptPalletizeResult } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatQty } from '../../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { receiptsApi, receivingKeys } from '../api';
import { ArticlePicker } from '../components/Pickers';
import type { ArticleOption } from '../master-data-port';

/**
 * FCL palletize (spec §5.7): container goods arrive loose and unlabelled. We
 * build N identical pallets and print their labels as ONE batch job — not N
 * requests that can fail halfway and leave "labels 1-4 printed, 5-7 not".
 */
export function PalletizePanel({ receipt }: { receipt: ReceiptDetail }): ReactNode {
  const { t } = useTranslation('receiving');
  const queryClient = useQueryClient();

  const [article, setArticle] = useState<ArticleOption | null>(null);
  const [lotNo, setLotNo] = useState('');
  const [bestBefore, setBestBefore] = useState('');
  const [palletCount, setPalletCount] = useState('1');
  const [qtyPerPallet, setQtyPerPallet] = useState('');
  const [printLabels, setPrintLabels] = useState(true);
  const [result, setResult] = useState<ReceiptPalletizeResult | null>(null);

  const closed = receipt.status === 'closed';
  const count = Number(palletCount);
  const per = Number(qtyPerPallet);
  const valid = Boolean(article) && Number.isFinite(count) && count >= 1 && per > 0;

  const palletize = useMutation({
    mutationFn: () =>
      receiptsApi.palletize(receipt.id, {
        articleId: article!.id,
        lotNo: lotNo.trim() || null,
        bestBefore: bestBefore || null,
        palletCount: Math.trunc(count),
        qtyPerPallet,
        printLabels,
        printerId: null,
      }),
    onSuccess: async (created) => {
      setResult(created);
      toastSuccess(t('palletize.created', { count: created.pallets.length }));
      // Printing is best-effort and reported honestly: the goods are on the
      // floor either way.
      toastInfo(created.printJobId ? t('palletize.printQueued') : t('palletize.printUnavailable'));
      await queryClient.invalidateQueries({ queryKey: receivingKeys.receipt(receipt.id) });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  return (
    <Surface className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-medium text-ink">{t('palletize.title')}</h3>
        <p className="mt-1 text-xs text-ink-muted">{t('palletize.hint')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <ArticlePicker
          label={t('palletize.article')}
          value={article?.id ?? null}
          onChange={setArticle}
          disabled={closed}
        />
        <FormField
          label={t('palletize.lot')}
          value={lotNo}
          onChange={(event) => {
            setLotNo(event.target.value);
          }}
          disabled={closed}
        />
        <FormField
          label={t('palletize.bestBefore')}
          type="date"
          value={bestBefore}
          onChange={(event) => {
            setBestBefore(event.target.value);
          }}
          disabled={closed}
        />
        <FormField
          label={t('palletize.palletCount')}
          type="number"
          min="1"
          max="200"
          step="1"
          inputMode="numeric"
          value={palletCount}
          onChange={(event) => {
            setPalletCount(event.target.value);
          }}
          disabled={closed}
        />
        <FormField
          label={t('palletize.qtyPerPallet')}
          type="number"
          min="0"
          step="0.001"
          inputMode="decimal"
          value={qtyPerPallet}
          onChange={(event) => {
            setQtyPerPallet(event.target.value);
          }}
          disabled={closed}
        />
        <label className="flex items-end gap-2 pb-2 text-sm text-ink">
          <input
            type="checkbox"
            className="size-4 rounded border-surface-border"
            checked={printLabels}
            disabled={closed}
            onChange={(event) => {
              setPrintLabels(event.target.checked);
            }}
          />
          {t('palletize.printLabels')}
        </label>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">
          {valid
            ? t('palletize.total', {
                qty: formatQty(String(Math.trunc(count) * per), { unit: article?.baseUnitCode ?? '' }),
              })
            : null}
        </p>
        <Button
          loading={palletize.isPending}
          disabled={closed || !valid}
          onClick={() => {
            palletize.mutate();
          }}
        >
          {t('palletize.submit')}
        </Button>
      </div>

      {result ? (
        <div className="rounded-lg bg-surface-subtle p-3">
          <div className="text-xs uppercase tracking-wide text-ink-muted">{t('palletize.lpns')}</div>
          <ul className="mt-1 flex flex-wrap gap-2 font-mono text-sm text-ink">
            {result.pallets.map((pallet) => (
              <li key={pallet.palletId} className="rounded bg-surface px-2 py-0.5">
                {pallet.lpn}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Surface>
  );
}
